import { useState } from "react";
import type { FileExplorerConfig } from "../scenario/types";
import { WindowTitlebar } from "./WindowTitlebar";
import { tx, txr } from "../lib/tx";

const FILE_ICON: Record<string, { ico: string; tint: string }> = {
  exe: { ico: "\u2699", tint: "#7fd1ff" },
  doc: { ico: "\u{1F4C4}", tint: "#5b9bff" },
  pdf: { ico: "\u{1F4D5}", tint: "#ff6a6a" },
  img: { ico: "\u{1F5BC}", tint: "#9ad36b" },
  zip: { ico: "\u{1F5DC}", tint: "#e0c24a" },
  folder: { ico: "\u{1F4C1}", tint: "#e8c15a" },
};

/**
 * A Windows-Explorer shell showing the freshly-saved attachment in Downloads.
 * The learner clicks (or double-clicks) the malicious file; a UAC-style "do you want to run
 * this?" confirm appears and — on confirm — the beat completes (App records
 * `executed_attachment`). The other files are inert. The dangerous file blinks
 * so a learner can't get lost.
 */
export function FileExplorer({
  config,
  onRun,
  onDecline,
}: {
  config: FileExplorerConfig;
  onRun: (fileId: string) => void;
  /** "No" on the UAC confirm (the course treats it as the safe choice). */
  onDecline?: () => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const files = config.files ?? [];

  const tryRun = (id: string) => {
    if (id !== config.runFileId) {
      // inert: just select; nothing happens (these are decoys).
      setSelected(id);
      return;
    }
    if (config.runConfirm) setConfirmId(id);
    else onRun(id);
  };

  return (
    <div className="winapp fileexp">
      <WindowTitlebar title={config.windowTitle ?? tx("File Explorer")} />
      <div className="fx-addressbar">
        <span className="fx-nav">{"\u2039"}</span>
        <span className="fx-nav">{"\u203A"}</span>
        <span className="fx-nav">{"\u2191"}</span>
        <span className="fx-path">{config.path ?? tx("This PC > Downloads")}</span>
        <span className="fx-search">{tx("Search Downloads")}</span>
      </div>

      <div className="fx-body">
        <div className="fx-side">
          <div className="fx-side-grp">{tx("Quick access")}</div>
          <div className="fx-side-item">{"\u{1F5A5}"} {tx("Desktop")}</div>
          <div className="fx-side-item active">{"\u2B07"} {tx("Downloads")}</div>
          <div className="fx-side-item">{"\u{1F4C4}"} {tx("Documents")}</div>
          <div className="fx-side-item">{"\u{1F5BC}"} {tx("Pictures")}</div>
        </div>

        <div className="fx-grid" role="list">
          {files.map((f) => {
            const meta = FILE_ICON[f.type ?? "doc"] ?? FILE_ICON.doc;
            // The run target keeps a pulsing background glow until the UAC confirm
            // opens, so a learner can't miss WHAT to double-click (and that they
            // must double-click). It no longer turns off on single-select.
            const glow = f.id === config.runFileId && !confirmId;
            return (
              <button
                key={f.id}
                role="listitem"
                type="button"
                className={`fx-file ${selected === f.id ? "active" : ""} ${f.danger ? "danger" : ""} ${glow ? "glow" : ""}`}
                onClick={() => tryRun(f.id)}
              >
                <span className="fx-file-ico" style={{ color: meta.tint }}>{meta.ico}</span>
                <span className="fx-file-name">{f.name}</span>
                <span className="fx-file-meta">
                  {f.date ?? ""}
                  {f.size ? ` \u00B7 ${f.size}` : ""}
                </span>
                {glow && <span className="fx-file-hint" aria-hidden>{tx("Click to open")}</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="fx-status">
        {txr("{length} item{v}{v2}<0>Click a file to open it</0>", { length: files.length, v: files.length === 1 ? "" : tx("s"), v2: selected ? tx(" · 1 selected") : "" }, [<span className="fx-hint" />])}
      </div>

      {/* UAC-style confirm */}
      {confirmId && config.runConfirm && (
        <div className="fx-uac">
          <div className="fx-uac-card">
            <div className="fx-uac-head">
              <span className="fx-uac-shield">{"\u{1F6E1}"}</span>
              <span>{config.runConfirm.title}</span>
            </div>
            <div className="fx-uac-body">{config.runConfirm.body}</div>
            <div className="fx-uac-actions">
              <button
                className="fx-uac-btn yes"
                onClick={() => {
                  const id = confirmId;
                  setConfirmId(null);
                  onRun(id);
                }}
              >
                {config.runConfirm.runLabel}
              </button>
              <button
                className="fx-uac-btn no"
                onClick={() => {
                  setConfirmId(null);
                  onDecline?.();
                }}
              >
                {config.runConfirm.cancelLabel ?? tx("No")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
